import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Link as TipTapLink } from '@tiptap/extension-link';
import { Image as TipTapImage } from '@tiptap/extension-image';
import api from '../services/api';
import '../pages/BlogAdmin.css';

// Toolbar Button Component
const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="editor-toolbar">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
        title="Bold"
        type="button"
      >
        <strong>B</strong>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
        title="Italic"
        type="button"
      >
        <em>I</em>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'is-active' : ''}
        title="Underline"
        type="button"
      >
        <u>U</u>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
        title="Strike"
        type="button"
      >
        <s>S</s>
      </button>
      
      <span className="separator"></span>
      
      <select
        onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
        value={editor.getAttributes('textStyle').fontFamily || ''}
        title="Font Family"
      >
        <option value="">Font Family</option>
        <option value="Arial">Arial</option>
        <option value="Georgia">Georgia</option>
        <option value="Times New Roman">Times New Roman</option>
        <option value="Verdana">Verdana</option>
      </select>
      
      <select
        onChange={(e) => {
          const size = e.target.value;
          if (size) {
            editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
          }
        }}
        value={editor.getAttributes('textStyle').fontSize || ''}
        title="Font Size"
      >
        <option value="">Font Size</option>
        <option value="12px">12px</option>
        <option value="14px">14px</option>
        <option value="16px">16px</option>
        <option value="18px">18px</option>
        <option value="20px">20px</option>
        <option value="24px">24px</option>
        <option value="30px">30px</option>
      </select>
      
      <input
        type="color"
        onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
        value={editor.getAttributes('textStyle').color || '#000000'}
        title="Text Color"
      />
      
      <button
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        className={editor.isActive('highlight') ? 'is-active' : ''}
        title="Highlight Color"
        type="button"
      >
        <span style={{ backgroundColor: '#ff0' }}>H</span>
      </button>
      
      <span className="separator"></span>
      
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        title="Bullet List"
        type="button"
      >
        • List
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        title="Numbered List"
        type="button"
      >
        1. List
      </button>
      
      <span className="separator"></span>
      
      <button
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
        title="Align Left"
        type="button"
      >
        ⬅️
      </button>
      <button
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
        title="Align Center"
        type="button"
      >
        ⬅️➡️
      </button>
      <button
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
        title="Align Right"
        type="button"
      >
        ➡️
      </button>
      <button
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
        title="Justify"
        type="button"
      >
        ≡
      </button>
      
      <span className="separator"></span>
      
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
        title="Heading 1"
        type="button"
      >
        H1
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
        title="Heading 2"
        type="button"
      >
        H2
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
        title="Heading 3"
        type="button"
      >
        H3
      </button>
      
      <span className="separator"></span>
      
      <button
        onClick={() => {
          const url = window.prompt('Enter URL:');
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        className={editor.isActive('link') ? 'is-active' : ''}
        title="Add Link"
        type="button"
      >
        🔗
      </button>
      <button
        onClick={() => editor.chain().focus().unsetLink().run()}
        title="Remove Link"
        type="button"
      >
        🔗❌
      </button>
      
      <button
        onClick={addImage}
        title="Add Image"
        type="button"
      >
        🖼️
      </button>
      
      <span className="separator"></span>
      
      <button
        onClick={() => editor.chain().focus().undo().run()}
        title="Undo"
        type="button"
      >
        ↩️
      </button>
      <button
        onClick={() => editor.chain().focus().redo().run()}
        title="Redo"
        type="button"
      >
        ↪️
      </button>
    </div>
  );
};

const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    image_url: '',
    is_published: true,
    slug: '',
    meta_description: '',
    tags: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      TextStyle,
      Color,
      FontFamily,
      Highlight,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TipTapLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      TipTapImage,
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setFormData(prev => ({
        ...prev,
        content: html
      }));
    },
  });

  useEffect(() => {
    if (id) {
      const fetchPost = async () => {
        setLoading(true);
        try {
          const data = await api.get(`/blogs/${id}`);
          setFormData(data);
          setImagePreview(data.image_url);
          if (editor && data.content) {
            editor.commands.setContent(data.content);
          }
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      };
      fetchPost();
    }
  }, [id, editor]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }
      
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  const uploadImage = async () => {
  if (!imageFile) return null;
  
  setUploadingImage(true);
  const formData = new FormData();
  formData.append('image', imageFile);
  
  try {
    // Use the upload method with FormData
    const response = await api.upload('/upload', formData);
    setUploadingImage(false);
    return response.image_url;
  } catch (err) {
    setUploadingImage(false);
    setError('Failed to upload image: ' + err.message);
    return null;
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      let imageUrl = formData.image_url;
      
      // Upload image if a new one is selected
      if (imageFile) {
        const uploadedUrl = await uploadImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        } else {
          setLoading(false);
          return;
        }
      }
      
      const submitData = {
        ...formData,
        image_url: imageUrl
      };
      
      if (id) {
        await api.put(`/blogs/${id}`, submitData);
      } else {
        await api.post('/blogs', submitData);
      }
      navigate('/bloglistadmin');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    setFormData(prev => ({
      ...prev,
      slug
    }));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({
      ...prev,
      image_url: ''
    }));
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="blog-form-container">
      <h2>{id ? 'Edit Blog Post' : 'Add New Blog Post'}</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
          <button 
            type="button" 
            onClick={generateSlug}
            className="btn-generate-slug"
          >
            Generate Slug
          </button>
        </div>
        
        <div className="form-group">
          <label>Slug</label>
          <input
            type="text"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="form-group">
          <label>Featured Image</label>
          <div className="image-upload-container">
            {imagePreview ? (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" className="preview-image" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="btn-remove-image"
                >
                  Remove Image
                </button>
              </div>
            ) : (
              <div className="image-upload-area">
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="image-upload" className="image-upload-label">
                  Click to upload image
                </label>
                <p className="upload-hint">Supported formats: JPG, PNG, GIF (Max 5MB)</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="form-group">
          <label>Content (Rich Text Editor)</label>
          <div className="tiptap-editor">
            <MenuBar editor={editor} />
            <EditorContent editor={editor} className="editor-content" />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Category</label>
            <input
              type="text"
              name="category"
              value={formData.category}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label>Meta Description</label>
            <textarea
              name="meta_description"
              value={formData.meta_description}
              onChange={handleChange}
              rows="3"
              maxLength="160"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Tags (comma separated)</label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              name="is_published"
              checked={formData.is_published}
              onChange={handleChange}
            />
            Published
          </label>
        </div>
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading || uploadingImage}
          >
            {loading || uploadingImage ? 'Saving...' : 'Save Post'}
          </button>
          <RouterLink to="/bloglistadmin" className="btn btn-cancel">
            Cancel
          </RouterLink>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;